import type {
  AIChatStreamEvent,
  AiUserPreferences,
  ClarifyingQuestionItem,
  ClarifyingQuestionOption,
  ClarifyingQuestionsData,
  ProgressStep,
  Question,
  SpeakingGuide,
  TopicDraft,
  UrlContentResult,
  Vocabulary,
  WebSearchResult,
  WebSearchResultItem,
} from "./types";
import { mergeProgressSteps } from "@/lib/topic/progress";

export { mergeProgressSteps };

const TOOL_EVENT_PREFIXES = new Set(["9", "a", "t", "c"]);

function parseJsonSafely(value: string): unknown | null {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function toWebSearchEvent(payload: unknown): AIChatStreamEvent | null {
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Record<string, unknown>;
  const maybeData = (record.data as Record<string, unknown> | undefined) ?? record;

  const query = typeof maybeData.query === "string" ? maybeData.query : null;
  const rawResults = Array.isArray(maybeData.results) ? maybeData.results : null;
  if (!query || !rawResults) return null;

  const results: WebSearchResultItem[] = rawResults
    .filter((r) => r && typeof r === "object")
    .map((r) => {
      const item = r as Record<string, unknown>;
      return {
        position: typeof item.position === "number" ? item.position : 0,
        site_name: typeof item.site_name === "string" ? item.site_name : "",
        title: typeof item.title === "string" ? item.title : "",
        snippet: typeof item.snippet === "string" ? item.snippet : "",
        url: typeof item.url === "string" ? item.url : "",
      };
    });

  const totalResults =
    typeof maybeData.total_results === "number"
      ? maybeData.total_results
      : results.length;

  // Empty result sets are still surfaced as a real event so the UI can show a
  // "no results" card. Only drop when we have neither results nor any error
  // information to display.
  const status =
    typeof maybeData.status === "string"
      ? (maybeData.status as WebSearchResult["status"])
      : results.length > 0
        ? "success"
        : "empty";

  const errorCode =
    typeof maybeData.errorCode === "string" ? maybeData.errorCode : undefined;
  const errorMessage =
    typeof maybeData.errorMessage === "string" ? maybeData.errorMessage : undefined;

  // Treat explicit `empty` from the upstream as a real, displayable outcome
  // even when the results array is empty.
  if (status === "empty" && results.length === 0) {
    const search: WebSearchResult = {
      query,
      results: [],
      total_results: 0,
      status: "empty",
    };
    return { type: "web-search", search };
  }

  if (status !== "error" && results.length === 0) {
    return null;
  }

  const search: WebSearchResult = {
    query,
    results,
    total_results: totalResults,
    status,
    ...(errorCode ? { errorCode: errorCode as WebSearchResult["errorCode"] } : {}),
    ...(errorMessage ? { errorMessage } : {}),
  };
  return { type: "web-search", search };
}

function toProgressEvent(payload: unknown): AIChatStreamEvent[] {
  const items = Array.isArray(payload) ? payload : [payload];
  const events: AIChatStreamEvent[] = [];

  for (const item of items) {
    if (!item || typeof item !== "object") {
      continue;
    }

    const record = item as Record<string, unknown>;
    const maybeType = typeof record.type === "string" ? record.type : undefined;
    const maybeData = (record.data as Record<string, unknown> | undefined) || record;

    if (maybeType !== "generation-progress" && maybeType !== "data-generation-progress") {
      continue;
    }

    if (
      typeof maybeData.step === "string" &&
      typeof maybeData.message === "string" &&
      typeof maybeData.completed === "boolean"
    ) {
      const progressStep: ProgressStep = {
        step: maybeData.step as ProgressStep["step"],
        message: maybeData.message,
        completed: maybeData.completed,
      };

      // A step that gave up rather than one still in flight. Absent on the
      // happy path, so only set it when the server actually said so.
      if (maybeData.failed === true) {
        progressStep.failed = true;
      }

      // Pass through metadata if present
      const maybeMetadata = maybeData.metadata as Record<string, string> | undefined;
      if (maybeMetadata && typeof maybeMetadata === "object") {
        progressStep.metadata = {
          title: maybeMetadata.title,
          category: maybeMetadata.category,
        };
      }

      events.push({
        type: "progress",
        progress: progressStep,
      });
    }
  }

  return events;
}

function toClarifyingQuestionsEvent(payload: unknown): AIChatStreamEvent | null {
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Record<string, unknown>;
  const d = (record.data as Record<string, unknown> | undefined) ?? record;

  if (!Array.isArray(d.questions) || d.questions.length === 0) return null;

  const seenQuestionIds = new Set<string>();
  const questions: ClarifyingQuestionItem[] = [];

  for (let i = 0; i < d.questions.length; i++) {
    const rawQ = d.questions[i];
    if (!rawQ || typeof rawQ !== "object") continue;
    const q = rawQ as Record<string, unknown>;
    const questionText = typeof q.question === "string" ? q.question.trim() : "";
    if (!questionText || !Array.isArray(q.options)) continue;

    const seenOptionLabels = new Set<string>();
    const options: ClarifyingQuestionOption[] = [];

    for (const rawOpt of q.options) {
      if (typeof rawOpt === "string") {
        const label = rawOpt.trim();
        if (!label) continue;
        const key = label.toLowerCase();
        if (seenOptionLabels.has(key)) continue;
        seenOptionLabels.add(key);
        options.push({ label });
      } else if (rawOpt && typeof rawOpt === "object") {
        const opt = rawOpt as Record<string, unknown>;
        const label = typeof opt.label === "string" ? opt.label.trim() : "";
        if (!label) continue;
        const key = label.toLowerCase();
        if (seenOptionLabels.has(key)) continue;
        seenOptionLabels.add(key);
        options.push({
          label,
          ...(typeof opt.description === "string" && opt.description.trim()
            ? { description: opt.description.trim() }
            : {}),
        });
      }
      if (options.length >= 5) break;
    }

    if (options.length < 2) continue;

    const baseId = typeof q.id === "string" && q.id.trim() ? q.id.trim() : `q-${i + 1}`;
    const uniqueId = seenQuestionIds.has(baseId) ? `${baseId}-${i + 1}` : baseId;
    seenQuestionIds.add(uniqueId);

    questions.push({
      id: uniqueId,
      question: questionText,
      options,
      ...(q.allowMultiple === true ? { allowMultiple: true } : {}),
    });

    if (questions.length >= 3) break;
  }

  if (questions.length === 0) return null;

  const data: ClarifyingQuestionsData = {
    ...(typeof d.title === "string" && d.title.trim() ? { title: d.title.trim() } : {}),
    questions,
  };

  return { type: "clarifying-questions", data };
}

function toTopicDraftEvent(payload: unknown): AIChatStreamEvent[] {
  if (!payload || typeof payload !== "object") {
    return [];
  }

  const record = payload as Record<string, unknown>;
  const result = (record.result as Record<string, unknown> | undefined) ?? null;

  if (
    !result ||
    typeof result.title !== "string" ||
    !Array.isArray(result.questions) ||
    !Array.isArray(result.vocabulary)
  ) {
    return [];
  }

  // Never render a draft card with zero questions: that shape means the
  // backend chain failed after vocab/guide and callers surface an error step
  // instead. Rendering it would strand the user on an unsavable draft
  // (create-topic validation requires min(1) question).
  if (result.questions.length === 0) {
    return [];
  }

  // Guard against misidentifying an askClarifyingQuestions tool output that
  // also carries `{ title, questions }`.
  const firstQuestion = result.questions[0] as Record<string, unknown> | undefined;
  if (firstQuestion && Array.isArray(firstQuestion.options)) {
    return [];
  }

  return [
    {
      type: "topic-draft",
      draft: result as unknown as TopicDraft,
    },
  ];
}

function toUrlContentEvent(payload: unknown): AIChatStreamEvent | null {
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Record<string, unknown>;
  const d = (record.data as Record<string, unknown> | undefined) ?? record;

  const url = typeof d.url === "string" ? d.url : "";
  if (!url) return null;

  const status: UrlContentResult["status"] =
    d.status === "success" || d.status === "empty" || d.status === "error"
      ? d.status
      : "success";

  const content: UrlContentResult = {
    url,
    status,
    title: typeof d.title === "string" ? d.title : undefined,
    siteName: typeof d.siteName === "string" ? d.siteName : undefined,
    byline: typeof d.byline === "string" ? d.byline : undefined,
    truncated: typeof d.truncated === "boolean" ? d.truncated : undefined,
    excerpt: typeof d.excerpt === "string" ? d.excerpt : undefined,
    // Persisted page text (capped server-side) — folded back into the wire
    // context on later turns so the agent keeps the link context after reload.
    text: typeof d.text === "string" ? d.text : undefined,
    errorMessage: typeof d.errorMessage === "string" ? d.errorMessage : undefined,
  };

  return { type: "url-content", content };
}

function parseObjectChunkPayload(payload: Record<string, unknown>): AIChatStreamEvent[] {
  const type = typeof payload.type === "string" ? payload.type : "";

  if (type === "text-delta" && typeof payload.delta === "string") {
    return [{ type: "text", content: payload.delta }];
  }

  // Detect webSearch tool calls as they start (args available but tool not yet
  // executed). Lets the UI show a "Searching..." placeholder immediately while
  // the actual fetch + TinyFish API call runs in the background.
  if (type === "tool-input-available" && payload.toolName === "webSearch") {
    const input = (payload.input as Record<string, unknown> | undefined) ?? {};
    const query = typeof input.query === "string" ? input.query : "";
    const toolCallId = typeof payload.toolCallId === "string" ? payload.toolCallId : "";
    if (query || toolCallId) {
      return [{ type: "web-search-pending", query, toolCallId }];
    }
    return [];
  }

  // Detect fetchUrl tool calls as they start so the UI can show a "Reading link…"
  // placeholder while the page is fetched + parsed in the background.
  if (type === "tool-input-available" && payload.toolName === "fetchUrl") {
    const input = (payload.input as Record<string, unknown> | undefined) ?? {};
    const url = typeof input.url === "string" ? input.url : "";
    const toolCallId = typeof payload.toolCallId === "string" ? payload.toolCallId : "";
    if (url || toolCallId) {
      return [{ type: "url-content-pending", url, toolCallId }];
    }
    return [];
  }

  if (type === "tool-output-available") {
    return toTopicDraftEvent({ result: payload.output });
  }

  if (type === "data-clarifying-questions") {
    const clarifyingEvent = toClarifyingQuestionsEvent(payload);
    return clarifyingEvent ? [clarifyingEvent] : [];
  }

  if (type === "data-url-content") {
    const contentEvent = toUrlContentEvent(payload);
    return contentEvent ? [contentEvent] : [];
  }

  if (type === "data-generation-progress" || type === "generation-progress") {
    return toProgressEvent(payload);
  }

  if (type === "data-web-search") {
    const searchEvent = toWebSearchEvent(payload);
    return searchEvent ? [searchEvent] : [];
  }

  if (type === "data-preferences-updated") {
    const d = (payload.data as Record<string, unknown> | undefined) ?? payload;
    if (d && typeof d.english_level === "string") {
      const preferences: AiUserPreferences = {
        native_language: typeof d.native_language === "string" ? d.native_language : null,
        english_level: d.english_level,
        interests: Array.isArray(d.interests) ? (d.interests as string[]) : [],
        practice_goal: typeof d.practice_goal === "string" ? d.practice_goal : null,
        provenance: d.provenance as AiUserPreferences["provenance"],
      };
      return [{ type: "preferences-updated", preferences }];
    }
    return [];
  }

  if (type === "data-conversation-title") {
    const d = (payload.data as Record<string, unknown> | undefined) ?? payload;
    if (d && typeof d.title === "string" && d.title.trim()) {
      return [{ type: "conversation-title", title: d.title.trim() }];
    }
    return [];
  }

  if (type === "data-topic-content") {
    const maybeData = (payload.data as Record<string, unknown> | undefined) ?? payload;
    if (
      maybeData &&
      Array.isArray(maybeData.questions) &&
      Array.isArray(maybeData.vocabulary) &&
      typeof maybeData.category === "string"
    ) {
      return [{
        type: "topic-content",
        data: {
          title: typeof maybeData.title === "string" ? maybeData.title : undefined,
          description: typeof maybeData.description === "string" ? maybeData.description : undefined,
          category: maybeData.category as string,
          bannerUrl: typeof maybeData.bannerUrl === "string" ? maybeData.bannerUrl : undefined,
          bannerAlt: typeof maybeData.bannerAlt === "string" ? maybeData.bannerAlt : undefined,
          questions: maybeData.questions as Question[],
          vocabulary: maybeData.vocabulary as Vocabulary[],
          speakingGuide: maybeData.speakingGuide as SpeakingGuide,
        },
      }];
    }
    return [];
  }

  if (type === "error") {
    // `errorText` is the AI SDK's own spelling (`UIMessageChunk`); `error` and
    // `message` are accepted for older/other producers. Reading only the latter
    // two turned every framework-level failure into "An unexpected error
    // occurred", discarding the friendly copy the edge function had just mapped.
    const errorMessage = typeof payload.errorText === "string"
      ? payload.errorText
      : typeof payload.error === "string"
        ? payload.error
        : typeof payload.message === "string"
          ? payload.message
          : "An unexpected error occurred";
    return [{ type: "error", message: errorMessage }];
  }

  if (type === "finish" || type === "abort") {
    return [{ type: "finish", payload }];
  }

  return [];
}

function parseLegacyProtocolLine(trimmedLine: string): AIChatStreamEvent[] {
  const separatorIndex = trimmedLine.indexOf(":");
  if (separatorIndex === -1) {
    return [];
  }

  const prefix = trimmedLine.slice(0, separatorIndex);
  const payloadRaw = trimmedLine.slice(separatorIndex + 1).trim();

  const payload = parseJsonSafely(payloadRaw);
  if (payload === null) {
    return [];
  }

  if (prefix === "0" && typeof payload === "string") {
    return [{ type: "text", content: payload }];
  }

  if (prefix === "3" && typeof payload === "string") {
    return [{ type: "error", message: payload }];
  }

  if (prefix === "2" || prefix === "d") {
    return toProgressEvent(payload);
  }

  if (TOOL_EVENT_PREFIXES.has(prefix)) {
    return toTopicDraftEvent(payload);
  }

  if (prefix === "e") {
    return [{ type: "finish", payload }];
  }

  return [];
}

export function parseAIChatStreamLine(line: string): AIChatStreamEvent[] {
  const trimmedLine = line.trim();
  if (!trimmedLine) {
    return [];
  }

  if (trimmedLine.startsWith("data:")) {
    const ssePayload = trimmedLine.slice(5).trim();
    if (!ssePayload || ssePayload === "[DONE]") {
      return [];
    }

    const parsedSsePayload = parseJsonSafely(ssePayload);
    if (parsedSsePayload && typeof parsedSsePayload === "object" && !Array.isArray(parsedSsePayload)) {
      return parseObjectChunkPayload(parsedSsePayload as Record<string, unknown>);
    }

    if (typeof parsedSsePayload === "string") {
      return parseLegacyProtocolLine(parsedSsePayload);
    }

    return [];
  }

  return parseLegacyProtocolLine(trimmedLine);
}

export function splitBufferedLines(
  existingBuffer: string,
  chunk: string,
): { lines: string[]; buffer: string } {
  const merged = `${existingBuffer}${chunk}`;
  const lines = merged.split("\n");
  return {
    lines: lines.slice(0, -1),
    buffer: lines[lines.length - 1] || "",
  };
}

/**
 * Normalize a stream error into a user-facing message. The backend already
 * maps its failures to friendly copy, but this is a safety net for raw text
 * arriving over the wire (older deployments, other functions): technical
 * dumps are replaced, genuinely human messages pass through.
 */
export function toUserFacingErrorMessage(raw: string): string {
  const text = (raw ?? "").trim();
  if (!text) {
    return "Something went wrong. Please try again.";
  }
  // Budget exhaustion is checked before the timeout branch below: its copy
  // ("took too long") is specific to generation and reads as retryable, unlike
  // the generic "interrupted / check your connection" message.
  if (/ran out of time|budget used|took too long/i.test(text)) {
    return "Generating that topic took too long. Please try again.";
  }
  if (/rate.?limit|429/i.test(text)) {
    return "The AI service is busy right now. Please wait a moment and try again.";
  }
  if (/overload|capacity|503|529/i.test(text)) {
    return "The AI service is temporarily overloaded. Please try again in a moment.";
  }
  if (/question generation|no.?object.?generated|generate topic|failed to generate/i.test(text)) {
    return "I couldn't generate the topic this time. Please try again.";
  }
  if (/fetch failed|failed to fetch|network|timeout|timed out/i.test(text)) {
    return "The request was interrupted. Please check your connection and try again.";
  }
  if (/unauthorized|forbidden|\b401\b|\b403\b/i.test(text)) {
    return "Your session expired. Please refresh the page and sign in again.";
  }
  // Stack traces, multi-line dumps, or very long blobs are never shown raw.
  if (text.includes("\n") || /Error:\s*\S|^\s*at\s+\S/i.test(text) || text.length > 200) {
    return "Something went wrong while generating a reply. Please try again.";
  }
  return text;
}

/**
 * Tool-call protocol the model occasionally emits into its visible text
 * channel (e.g. a stray closing tag when a tool call is aborted mid-flight).
 * Rendered raw it looks like broken markup to the learner, so strip it.
 *
 * Applied to the *accumulated* assistant text rather than per chunk, so a tag
 * split across two SSE chunks is still removed once it closes.
 */
const TOOL_PROTOCOL_TAG = /<\/?\s*(?:tool_call|tool_calls|tool_use|tool_result|function_call|function_calls|parameter)\b[^>]*>?/gi;

/**
 * Copy shown in place of a tool call that arrived as text. Mirrors
 * `supabase/functions/ai-chat/tool-call-guard.ts`, which suppresses the same
 * thing server-side; this is the backstop for a turn persisted before that
 * guard existed, or a stream from a deployment that predates it.
 */
export const TOOL_CALL_AS_TEXT_MESSAGE =
  "The assistant couldn't complete that request. Please try again.";

const TOOL_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_.-]{0,63}$/;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/**
 * Read a string that is *entirely* a tool-call envelope —
 * `{"name": "webSearch", "parameters": {...}}` — into its name and arguments.
 * Prose that merely mentions a call does not match.
 *
 * A model without native function calling writes its invocation into the text
 * channel and stops, so this shape *is* the assistant's entire reply. The name
 * has to satisfy the function-name grammar, which is what keeps a legitimate
 * `{"name": "Ada Lovelace"}` answer from matching.
 */
export function parseToolCallEnvelope(text: string): { name: string; parameters: Record<string, unknown> } | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }

  if (!isPlainObject(parsed)) return null;

  const name = parsed.name;
  if (typeof name !== "string" || !TOOL_NAME_PATTERN.test(name)) return null;

  // `parameters` is the AI SDK/Anthropic spelling; `arguments` is OpenAI's.
  const parameters = parsed.parameters ?? parsed.arguments;
  if (!isPlainObject(parameters)) return null;

  return { name, parameters };
}

export function sanitizeAssistantText(text: string): string {
  if (!text) return "";
  // A reply that is nothing but a tool call has no answer behind it — the call
  // never ran, so there is no follow-up to wait for. Say so instead of printing
  // the JSON at the learner.
  if (parseToolCallEnvelope(text)) {
    return TOOL_CALL_AS_TEXT_MESSAGE;
  }
  // Collapse the blank lines an unbalanced tag leaves behind before trimming.
  return text.replace(TOOL_PROTOCOL_TAG, "").replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Progress steps that only appear while the `createCompleteTopic` chain is
 * running. Seeing one means the assistant *started* building a topic.
 */
const TOPIC_GENERATION_STEPS: ReadonlySet<string> = new Set<ProgressStep["step"]>([
  "image",
  "topic",
  "vocabulary",
  "speaking-guide",
  "questions",
  "complete",
]);

/** True when this progress step came from the topic-generation chain. */
export function isTopicGenerationStep(step: ProgressStep["step"]): boolean {
  return TOPIC_GENERATION_STEPS.has(step);
}

export interface TopicGenerationOutcome {
  /** A progress step from the topic-generation chain was seen. */
  sawGenerationStep: boolean;
  /** The chain already reported its own failure (an `error` step landed). */
  sawGenerationError: boolean;
  /** Number of `topic-draft` events received during this stream. */
  draftCount: number;
}

/**
 * True when topic generation started but never produced a draft.
 *
 * A severed SSE connection resolves `reader.read()` with `done: true` — the
 * same value a clean finish produces — so a stream that dies mid-generation is
 * indistinguishable from a finished reply at the transport level. There is no
 * thrown error and no `error` event to catch, which used to leave the turn
 * ending silently with no way to create the topic. This is the signal that
 * turns that dead end into a visible, retryable failure.
 */
export function hasUnfinishedTopicGeneration({
  sawGenerationStep,
  sawGenerationError,
  draftCount,
}: TopicGenerationOutcome): boolean {
  return sawGenerationStep && !sawGenerationError && draftCount === 0;
}

/** Copy shown when topic generation was cut short before producing a draft. */
export const TOPIC_GENERATION_CUT_SHORT_MESSAGE =
  "Topic generation didn't finish — the connection was cut before the topic was ready. Please try again.";

/**
 * How long to wait for the server to close the stream after it has already
 * reported a generation failure.
 *
 * A tool that throws mid-stream emits its error chunk but the connection can
 * stay open with nothing further to send, so `reader.read()` never resolves.
 * That leaves `isStreaming` true forever: the composer is disabled, the stop
 * button is stuck, and the turn is unrecoverable without a page reload. This
 * grace period gives a well-behaved server a moment to close cleanly, then
 * releases the UI regardless.
 */
export const GENERATION_ERROR_STREAM_GRACE_MS = 6_000;
